import { dropTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import { PhX } from 'ember-phosphor-icons';
import {
  phoneCapturePromptShownRequest,
  snoozePhoneCapturePromptRequest,
  type PhoneCapturePromptSnoozeKind,
} from 'tio-data/requests/phone-capture-prompt';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import Drawer from 'tio-ui/components/drawer';
import matchMedia from 'tio-common/helpers/match-media';
import Modal from 'tio-ui/components/modal';
import PhoneVerificationFlow from '../phone-verification/flow';
import type { Store as StoreService } from '@warp-drive/core';
import type { TOC } from '@ember/component/template-only';

/** Above this the prompt is a centred modal; below it, a bottom sheet. */
export const DESKTOP_QUERY = '(min-width: 640px)';

/**
 * Reports the render exactly once. The cadence advances on the prompt actually
 * appearing, not on eligibility being read, so this cannot live in a getter.
 */
const reportShown = modifier((_element: HTMLElement, [notify]: [() => void]) => {
  notify();
});

interface PromptBodySignature {
  Args: {
    /** `desktop` or `mobile`; only distinguishes the two chromes in tests. */
    variant: string;
    onClose: () => void;
    onRemindLater: () => void;
    /** Verified or deferred — either way prompting is over. */
    onFinish: () => void;
    onShown: () => void;
    isSnoozing: boolean;
  };
}

/** One body for both chromes, so the two cannot drift apart. */
const PromptBody: TOC<PromptBodySignature> = <template>
  <div class="tio-col-loose" data-test-phone-capture-prompt={{@variant}} {{reportShown @onShown}}>
    <div class="tio-flex-end">
      <Button
        @appearance="minimal"
        @size="sm"
        {{on "click" @onClose}}
        aria-label={{t "common.close"}}
        data-test-phone-capture-prompt-close
      >
        <PhX @size={{16}} />
      </Button>
    </div>

    <p class="tio-text-sm-normal" data-test-phone-capture-prompt-benefit>
      {{t "phone_verification.prompt.benefit"}}
    </p>

    <PhoneVerificationFlow
      @surface="postLoginOverlay"
      @onVerified={{@onFinish}}
      @onDeferred={{@onFinish}}
    />

    <hr />

    <div class="tio-col-flush">
      <Button
        @appearance="minimal"
        @size="sm"
        @isRunning={{@isSnoozing}}
        {{on "click" @onRemindLater}}
        data-test-phone-capture-prompt-remind-later
      >{{t "phone_verification.prompt.remind_later"}}</Button>
      <p class="tio-text-xs-normal tio-fg-muted">{{t "phone_verification.prompt.profile_note"}}</p>
    </div>
  </div>
</template>;

export interface PhoneCapturePromptSignature {
  Args: {
    /**
     * Called once the prompt has finished with itself — dismissed, verified or
     * deferred. The caller hides it without a page reload; the cadence is already
     * persisted by then.
     */
    onDone: () => void;
  };
}

/**
 * The post-login mobile-capture prompt: a desktop modal or a mobile bottom sheet
 * around the shared verification flow.
 *
 * Every render gate lives in the caller (eligibility, capability, terms, demo
 * sessions) so this is only mounted when it should already be visible.
 */
export default class PhoneCapturePrompt extends Component<PhoneCapturePromptSignature> {
  @service declare store: StoreService;

  desktopQuery = DESKTOP_QUERY;

  /** A failed report must not hide the prompt — the member still sees a usable one. */
  announce = dropTask(async () => {
    try {
      await this.store.request(phoneCapturePromptShownRequest(this.platform));
    } catch {
      // The cadence tolerates a missed render; an unusable prompt does not.
    }
  });

  /**
   * Awaited before teardown, so a dismissal followed immediately by a reload still
   * finds the snooze recorded. A rejected snooze still closes — leaving the member
   * trapped in a prompt they dismissed is the worse failure.
   */
  snooze = dropTask(async (kind: PhoneCapturePromptSnoozeKind) => {
    try {
      await this.store.request(snoozePhoneCapturePromptRequest(kind, this.platform));
    } catch {
      // A lost snooze only re-offers the prompt at the next checkpoint.
    } finally {
      this.args.onDone();
    }
  });

  get platform(): string {
    return window.matchMedia(DESKTOP_QUERY).matches ? 'desktop' : 'mobile';
  }

  /**
   * Crossing the 640px breakpoint tears down one chrome and inserts the other, which
   * re-runs the insert modifier. The cadence counts prompts, not chromes.
   */
  hasReportedShown = false;

  notifyShown = () => {
    if (this.hasReportedShown) return;

    this.hasReportedShown = true;
    this.announce.perform();
  };

  /** `×` means "not now" — the next cadence checkpoint re-offers it. */
  dismissSoftly = () => {
    this.snooze.perform('soft');
  };

  /** "Remind me later" is the deliberate, longer suppression. */
  remindLater = () => {
    this.snooze.perform('long');
  };

  /** Acquisition ends prompting; the transitions consumer completes the cadence row. */
  finish = () => {
    this.args.onDone();
  };

  <template>
    {{#if (matchMedia this.desktopQuery)}}
      <Modal
        @isOpen={{true}}
        @onClose={{this.dismissSoftly}}
        @size="lg"
        @allowCloseButton={{false}}
        @ariaLabel={{t "phone_verification.number.title"}}
      >
        <:body>
          <PromptBody
            @variant="desktop"
            @onClose={{this.dismissSoftly}}
            @onRemindLater={{this.remindLater}}
            @onFinish={{this.finish}}
            @onShown={{this.notifyShown}}
            @isSnoozing={{this.snooze.isRunning}}
          />
        </:body>
      </Modal>
    {{else}}
      {{! manageHistory defaults to true, which lets history.back() dismiss the sheet —
          the wrong shape for a prompt whose dismissal has to be recorded. }}
      <Drawer
        @isOpen={{true}}
        @onClose={{this.dismissSoftly}}
        @placement="bottom"
        @size="lg"
        @manageHistory={{false}}
        @allowCloseButton={{false}}
        @ariaLabel={{t "phone_verification.number.title"}}
      >
        <:body>
          <PromptBody
            @variant="mobile"
            @onClose={{this.dismissSoftly}}
            @onRemindLater={{this.remindLater}}
            @onFinish={{this.finish}}
            @onShown={{this.notifyShown}}
            @isSnoozing={{this.snooze.isRunning}}
          />
        </:body>
      </Drawer>
    {{/if}}
  </template>
}
